import React, { useContext, useEffect, useReducer } from 'react';
import axios from 'axios';
import { Store } from '../Store';
import { getError } from '../utils';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MessageBox';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import { Helmet } from 'react-helmet-async';

const reducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_REQUEST':
            return { ...state, loading: true };
        case 'FETCH_SUCCESS':
            return { ...state, data: action.payload, loading: false };
        case 'FETCH_FAIL':
            return { ...state, loading: false, error: action.payload };
        default:
            return state;
    }
};

export default function BalanceSheetScreen() {
    const [{ loading, error, data }, dispatch] = useReducer(reducer, {
        loading: true,
        error: '',
        data: {},
    });

    const { state } = useContext(Store);
    const { userInfo } = state;

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch({ type: 'FETCH_REQUEST' });
                const { data } = await axios.get(
                    `/api/report/balance-sheet`,
                    { headers: { Authorization: `Bearer ${userInfo.token}` } }
                );
                dispatch({ type: 'FETCH_SUCCESS', payload: data });
            } catch (err) {
                dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
            }
        };
        fetchData();
    }, [userInfo]);

    return (
        <Container className="py-4">
            <Helmet>
                <title>Balance Sheet</title>
            </Helmet>
            <h1 className="mb-4">Balance Sheet</h1>

            {loading ? (
                <LoadingBox />
            ) : error ? (
                <MessageBox variant="danger">{error}</MessageBox>
            ) : (
                <Card className="shadow-sm border-0">
                    <Card.Body className="p-4 p-md-5">
                        <div className="text-center mb-5">
                            <h2 className="fw-bold text-uppercase">{userInfo.restaurantName || 'Business'} Statement of Financial Position</h2>
                            <p className="text-muted text-uppercase">As of {new Date(data.asOf).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                        </div>

                        <Row className="gy-5">
                            {/* ASSETS COLUMN */}
                            <Col lg={6} className="border-end-lg">
                                <Table borderless className="mb-0">
                                    <tbody>
                                        <tr>
                                            <td colSpan="2" className="fw-bold fs-5 border-bottom pb-2 text-primary">ASSETS</td>
                                        </tr>
                                        <tr>
                                            <td colSpan="2" className="fw-bold pt-3 ps-2">Current Assets</td>
                                        </tr>
                                        <tr>
                                            <td className="ps-4 py-2">Cash and Cash Equivalents</td>
                                            <td className="text-end py-2">{data.assets.cash.toLocaleString()} RWF</td>
                                        </tr>
                                        <tr>
                                            <td className="ps-4 py-2">Inventory</td>
                                            <td className="text-end py-2">{data.assets.inventory.toLocaleString()} RWF</td>
                                        </tr>
                                        <tr>
                                            <td className="ps-4 py-2">Accounts Receivable (Debts)</td>
                                            <td className="text-end py-2">{data.assets.receivables.toLocaleString()} RWF</td>
                                        </tr>
                                        <tr className="bg-light fw-bold border-top mt-3">
                                            <td className="ps-2">TOTAL ASSETS</td>
                                            <td className="text-end">{data.assets.totalAssets.toLocaleString()} RWF</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Col>

                            {/* LIABILITIES & EQUITY COLUMN */}
                            <Col lg={6}>
                                <Table borderless className="mb-4">
                                    <tbody>
                                        <tr>
                                            <td colSpan="2" className="fw-bold fs-5 border-bottom pb-2 text-danger">LIABILITIES</td>
                                        </tr>
                                        <tr>
                                            <td colSpan="2" className="fw-bold pt-3 ps-2">Current Liabilities</td>
                                        </tr>
                                        <tr>
                                            <td className="ps-4 py-2">Accounts Payable</td>
                                            <td className="text-end py-2">{data.liabilities.payables.toLocaleString()} RWF</td>
                                        </tr>
                                        <tr className="bg-light fw-bold border-top">
                                            <td className="ps-2">TOTAL LIABILITIES</td>
                                            <td className="text-end">{data.liabilities.totalLiabilities.toLocaleString()} RWF</td>
                                        </tr>
                                    </tbody>
                                </Table>

                                <Table borderless>
                                    <tbody>
                                        <tr>
                                            <td colSpan="2" className="fw-bold fs-5 border-bottom pb-2 text-success">SHAREHOLDERS' EQUITY</td>
                                        </tr>
                                        <tr>
                                            <td className="ps-4 py-3">Retained Earnings</td>
                                            <td className="text-end py-3">{data.equity.retainedEarnings.toLocaleString()} RWF</td>
                                        </tr>
                                        <tr className="bg-light fw-bold border-top">
                                            <td className="ps-2">TOTAL EQUITY</td>
                                            <td className="text-end">{data.equity.totalEquity.toLocaleString()} RWF</td>
                                        </tr>
                                    </tbody>
                                </Table>

                                <Table borderless className="mt-5">
                                    <tbody>
                                        <tr className="fs-4 fw-bold bg-dark text-white rounded">
                                            <td className="py-3 px-3">TOTAL LIABILITIES & EQUITY</td>
                                            <td className="text-end py-3 px-3">{(data.liabilities.totalLiabilities + data.equity.totalEquity).toLocaleString()} RWF</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>

                        <div className="mt-5 pt-4 text-center text-muted small border-top">
                            <p className="mb-1 fw-bold">Accounting Equation: Assets = Liabilities + Equity</p>
                            <p>{data.assets.totalAssets.toLocaleString()} = {(data.liabilities.totalLiabilities + data.equity.totalEquity).toLocaleString()}</p>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
}
